import { ActionBus, Secret } from 'game-logic';
import {
  BehaviorSubject,
  interval,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { GameBehavior, GameBehaviorActions } from './game-behavior';
import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { GameId } from 'src/app/model/game/game-id';
import { WagerApi } from 'src/app/core/api/wager-api';
import { Stopwatch } from 'src/app/shared/utils/stopwatch';

export class PracticeBehavior implements GameBehavior {
  get logicVersion(): number {
    return 1;
  }
  get currentRoomId(): string {
    return '';
  }
  get roundDeadline(): number {
    return this.deadline || 60000;
  }
  get behaviorActions(): ActionBus<GameBehaviorActions> {
    return this.behaviorActionBus;
  }
  public get formattedReward$(): Observable<string | undefined> {
    return this.formattedReward.asObservable();
  }
  public get opponent$(): Observable<string | undefined> {
    return this.opponent.asObservable();
  }
  public get opponentId$(): Observable<string | undefined> {
    return this.opponentId.asObservable();
  }
  public get bountyAmount$(): Observable<number | undefined> {
    return of(undefined);
  }

  private secrets: Secret[] = [];
  private behaviorActionBus = new ActionBus<GameBehaviorActions>();
  private formattedReward = new BehaviorSubject<string | undefined>(undefined);
  private opponent = new BehaviorSubject<string | undefined>(undefined);
  private opponentId = new BehaviorSubject<string | undefined>(undefined);
  private timerSubscription: Subscription | null = null; // To track the active timer
  private destroy$ = new Subject<void>();

  private deadline: number | undefined;

  constructor(private services: UxGameServices) {}

  // --------------

  public async createGame(
    gameId: GameId,
    reward: string | undefined,
  ): Promise<{
    opponentId: string | undefined;
    opponentName: string;
    opponentScore: number;
  }> {
    const practice = await WagerApi.request(this.services.httpService).randomPractice(gameId);
    this.secrets = practice.data.secrets;

    this.formattedReward.next(reward);
    this.opponent.next(practice.data.opponentName);
    this.opponentId.next(practice.data.opponentUserId);

    this.behaviorActionBus.emit('showStartButton', { show: false });
    this.behaviorActionBus.emit('autoStartGame');

    return {
      opponentId: practice.data.opponentUserId,
      opponentName: practice.data.opponentName,
      opponentScore: practice.data.opponentScore,
    };
  }

  // --------------

  public async startRoom(): Promise<void> {
    // Nothing to do for practice rounds
  }

  public onDestroy(): void {
    this.stopTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async onStartNewGame(): Promise<void> {
    this.deadline = Date.now() + 6 * 60 * 1000;
    this.startTimeout(this.deadline);
  }

  public onPauseGame(pause: boolean): void {
    if (pause) {
      this.stopTimeout();
      return;
    }
    this.startTimeout(this.deadline ?? 0);
  }

  public onStopGame(): void {
    this.stopTimeout();
  }

  public async getInitialMoves(): Promise<string[]> {
    return [];
  }

  public getMoves(): string[] {
    return [];
  }

  public async setMoves(_moves: string[]): Promise<boolean> {
    // Nothing to store for practice rounds
    return true;
  }

  public async discloseSecrets(
    _indexes: number[],
    _gameHash: string,
    _disclosedSecrets: number,
    _existingDeal: boolean,
  ): Promise<Secret[]> {
    return this.secrets;
  }

  private startTimeout(roundDeadline: number) {
    this.stopTimeout();

    this.timerSubscription = interval(1000)
      .pipe(
        startWith(0),
        map(() => Stopwatch.toSecs(roundDeadline - Date.now())),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (remaining) => {
          if (remaining <= 0) {
            this.behaviorActionBus.emit('onCountdownChanged', { countdown: 0 });
            this.behaviorActionBus.emit('onDeadlineExpired');
            this.timerSubscription?.unsubscribe(); // Manually complete
          } else {
            this.behaviorActionBus.emit('onCountdownChanged', { countdown: remaining });
          }
        },
      });
  }

  private stopTimeout() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe(); // Unsubscribe from the current timer
      this.timerSubscription = null;
    }
  }
}
